import dayjs from "dayjs";

import type { Frequency, ScheduleFormValues } from "../../types";

import getNextRecurrenceDate from "./getNextRecurrenceDate";

type Params = {
  scheduleId?: string;
  startDate: string | undefined;
  distributionFrequency: Frequency | undefined;
  initialValues: Pick<ScheduleFormValues, "startDate" | "nextDate">;
};

const isSameCalendarDay = (left: string, right: string): boolean => {
  const leftDay = dayjs(left).startOf("day");
  const rightDay = dayjs(right).startOf("day");

  return leftDay.isSame(rightDay);
};

const computeCalculatedNextDate = (
  startDate: string | undefined,
  frequency: Frequency | undefined,
): string | undefined => {
  const hasMissingRecurrenceInput = !startDate || !frequency;

  if (hasMissingRecurrenceInput) {
    return undefined;
  }

  return getNextRecurrenceDate(startDate, frequency);
};

const isStartDateUnchangedFromInitial = (
  startDate: string | undefined,
  initialStartDate: string | undefined,
): boolean => {
  const hasMissingStartForComparison = !startDate || !initialStartDate;

  if (hasMissingStartForComparison) {
    return false;
  }

  return isSameCalendarDay(startDate, initialStartDate);
};

const getNextDate = ({
  scheduleId,
  startDate,
  distributionFrequency,
  initialValues,
}: Params): string | undefined => {
  const calculatedNextDate = computeCalculatedNextDate(
    startDate,
    distributionFrequency,
  );

  const isEditingExistingSchedule = Boolean(scheduleId);

  const isStartDateUnchanged = isStartDateUnchangedFromInitial(
    startDate,
    initialValues.startDate,
  );
  
  const shouldUseDocumentNextDate =
    isEditingExistingSchedule && isStartDateUnchanged;

  const documentNextDate = initialValues.nextDate;

  const resolvedNextDate = shouldUseDocumentNextDate
    ? (documentNextDate ?? calculatedNextDate)
    : calculatedNextDate;

  return resolvedNextDate;
};

export default getNextDate;
